import {DEFAULT_CONTAINER_HEIGHT, RENDER_FORMATS, STATUS_TYPES} from '../../render/constants'
import flatMap from 'lodash.flatmap'
import type atlas from 'azure-maps-control'
import {
  Map,
  source as atlas_source,
  data,
  layer,
  control,
  Popup,
  AuthenticationType,
  ControlPosition,
  Shape as AtlasMarkerShape,
} from 'azure-maps-control'
import {feature} from 'topojson-client'
import DOMPurify from 'dompurify'
import '../../../stylesheets/azure-maps-geojson.scss'
import {assertHTMLElement, invariant} from '../../render/utils'
import Status from '../../render/status'
import type {Objects, Topology, TopoJSON} from 'topojson-specification'
import type {GeoJsonProperties, FeatureCollection, Position} from 'geojson'

class GeoJSONTypeMissingError extends Error {}

const asTopology = (geojson: TopoJSON) => geojson as unknown as Topology<Objects<GeoJsonProperties>>

class GeoJSONRenderer {
  el: HTMLElement
  map: atlas.Map
  status: Status

  constructor() {
    const map = document.getElementById('js-map')
    invariant(assertHTMLElement(map))

    this.el = map

    const basemap = this.el.getAttribute('data-basemap') ?? 'road'
    const azureClientId = this.el.getAttribute('data-client-id') ?? ''
    const azureToken = this.el.getAttribute('data-token') ?? ''

    this.map = new Map('js-map', {
      // Longitude, Latitude
      center: [0, 0],
      zoom: 20,
      language: 'en-US',
      authOptions: {
        authType: AuthenticationType.anonymous,
        clientId: azureClientId,
        getToken: async resolve => {
          return resolve(azureToken)
        },
      },
      style: basemap,
    })
    this.status = new Status(RENDER_FORMATS.geojson)
  }

  loadFiles() {
    const urls = this.srcUrls()

    for (const url of Object.values(urls)) {
      if (!url) {
        continue
      }

      this.status.load(url, {
        json: true,
        success: respData => this.render(respData as TopoJSON),
        error: (error: Error, xhr: Response) => {
          this.error(xhr, 'test error', error)
        },
      })
    }
  }

  srcUrls() {
    return {view: this.el.getAttribute('data-file')}
  }

  #isSinglePointCoordinate(coord: unknown[]) {
    return coord.length === 2 && coord.every(decimalDegree => !Number.isNaN(Number(decimalDegree)))
  }

  // Render a GeoJSON payload to a map
  render(geojson: TopoJSON) {
    this.map.events.add('ready', () => {
      const source = new atlas_source.DataSource(undefined, {
        cluster: true,
        clusterRadius: 45,
        clusterZoom: 15,
      })
      /**
       * We have to create a new datasource, becuase sources with
       * clustering enabled do not respect polygon placement.
       * This also means that when we loop through the shapes, we need
       * to explicitly add polygons to the map as features.
       *
       * Without clustering enabled, the azure map sdk would perform this
       * for us.
       */
      const polygonSource = new atlas_source.DataSource()
      const lineSource = new atlas_source.DataSource()

      if (this.getGeoJSONType(geojson) === 'topology') {
        const topojson = asTopology(geojson)
        let featuresArray: Array<data.Feature<data.Geometry, unknown>> = []
        for (const [, value] of Object.entries(topojson.objects)) {
          const d = feature(topojson, value)
          if (d?.type === 'FeatureCollection') {
            const features = (d as FeatureCollection).features
            featuresArray = featuresArray.concat(features as Array<data.Feature<data.Geometry, unknown>>)
          } else {
            featuresArray.push(d as data.Feature<data.Geometry, unknown>)
          }
        }

        source.add(featuresArray)
        this.map.setCamera({
          bounds: data.BoundingBox.fromData(featuresArray),
        })
      } else {
        if ('features' in geojson) {
          source.add((geojson as unknown as data.FeatureCollection).features)
        } else {
          source.add(geojson as data.Geometry)
        }

        // Coordinates of bounding box in which to constrain the viewable map area to.
        const bounds = data.BoundingBox.fromData(geojson as data.Geometry)

        // Expand the bounding coordinates if a single point is provided to make sure we show enough of the map to users
        // Not doing this will keep the bounds to just a single point causing the map to be completely zoomed into that point
        if (geojson?.type === 'Point') {
          bounds[0] = bounds[0] - 0.0055
          bounds[1] = bounds[1] - 0.0055
          bounds[2] = bounds[2] + 0.0055
          bounds[3] = bounds[3] + 0.0055
        }

        this.map.setCamera({
          padding: 100,
          bounds,
        })
      }
      this.map.sources.add(source)
      this.map.sources.add(polygonSource)
      this.map.sources.add(lineSource)
      const bubbleLayer = new layer.BubbleLayer(source, undefined, {
        //Scale the size of the clustered bubble based on the number of points in the cluster.
        radius: [
          'step',
          ['get', 'point_count'],
          20, //Default of 20 pixel radius.
          100,
          30, //If point_count >= 100, radius is 30 pixels.
          750,
          40, //If point_count >= 750, radius is 40 pixels.
        ],

        //Change the color of the cluster based on the value on the point_cluster property of the cluster.
        color: '#4183c4',
        strokeWidth: 3,
        strokeColor: '#2a72b2',
        filter: ['has', 'point_count'], // Only rendered data points which have a point_count property, which clusters do.
      })

      this.map.layers.add([
        bubbleLayer,
        // Create a symbol layer to render the count of locations in a cluster.
        new layer.SymbolLayer(source, undefined, {
          iconOptions: {
            image: 'none', //Hide the icon image.
          },
          textOptions: {
            textField: ['get', 'point_count_abbreviated'],
            offset: [0, 0.4],
            color: 'white',
          },
        }),
        // Create a layer to render the individual locations.
        new layer.SymbolLayer(source, undefined, {
          filter: ['!', ['has', 'point_count']], // Filter out clustered points from this layer.
        }),
      ])

      this.map.controls.add(new control.ZoomControl(), {
        position: ControlPosition.TopLeft,
      })

      // Create a popup but leave it closed so we can update it and display it later.
      const popup = new Popup({
        pixelOffset: [0, -18],
        closeButton: false,
      })

      const shapes = source.getShapes()

      for (const shape of shapes) {
        try {
          const geojsonType = shape.getType()

          const coords = shape.getCoordinates()

          if (geojsonType === 'LineString') {
            lineSource.add(new data.Feature(new data.LineString(coords as Position[])))
          } else if (geojsonType === 'Polygon') {
            polygonSource.add(new data.Feature(new data.Polygon(coords as Position[])))
          } else if (geojsonType === 'MultiPolygon') {
            polygonSource.add(new data.Feature(new data.MultiPolygon(coords as Position[][][])))
          }
        } catch (e) {
          window.debug(e)
        }
      }

      this.map.layers.add(
        new layer.PolygonLayer(polygonSource, undefined, {
          fillColor: 'blue',
          fillOpacity: 0.3,
        }),
        'labels',
      )

      this.map.layers.add(
        new layer.LineLayer(lineSource, undefined, {
          strokeColor: '#2a72b2',
        }),
      )

      this.map.events.add('mouseover', function () {
        popup.close()
      })

      // Add a click event to open a popup
      this.map.events.add('click', e => {
        if (e.shapes?.length === 0) {
          return
        }

        const markerShape = e.shapes?.[0]

        if (!markerShape || !(markerShape instanceof AtlasMarkerShape)) {
          return
        }

        if (typeof markerShape.getCoordinates !== 'function' || typeof markerShape.getProperties !== 'function') {
          return
        }

        const properties = markerShape.getProperties()

        if (Object.keys(properties).length === 1 && '_azureMapsShapeId' in properties) {
          // We dont want to show an empty popup, bail early if there is no content to display.
          return
        }

        // Coordinates can be a variety of nested arrays, but the popup will
        // only support a coordinate that represents a single point, e.g. [0, 0].
        const coordinate = flatMap(markerShape.getCoordinates())

        if (!this.#isSinglePointCoordinate(coordinate)) {
          // Attempting to open a popup for a coordinate that doesn't represent a single point
          // throws an error e.g. if a user clicks on a polygon. Bail early if the coordinate doesn't
          // represent a single point.
          return
        }

        popup.setOptions({
          // Update the content of the popup.
          content: this.createPopupElement(markerShape.getProperties()),
          // Update the popup's position with the symbol's coordinate.
          position: coordinate,
        })

        popup.open(this.map)
      })
    })

    this.status.set(STATUS_TYPES.ready)
    this.status.set(STATUS_TYPES.resize, {height: DEFAULT_CONTAINER_HEIGHT})
  }

  // Handle errors resulting from fetching JSON
  // Sends a message to the parent window so it can present the user with a
  // useful messages
  //
  // response - The response object
  // name - The name of the error
  // error - The error object
  //
  // Returns nothing
  error(_response: Response, _name: string, error: Error) {
    if (error instanceof SyntaxError) {
      this.status.set(STATUS_TYPES.fatal)
    }

    return window.debug(error)
  }

  createPopupElement(properties: Record<string, string> = {}) {
    if (Object.keys(properties).length === 0) {
      return ''
    }

    let table = '<table>'
    for (const token of Object.keys(properties)) {
      switch (token) {
        case '_azureMapsShapeId':
          continue
      }
      const value = properties[token]
      table += `<tr>
                  <th>${this.sanitize(token)}</th>
                  <td>${this.sanitize(value)}</td>
                </tr>`
    }
    table += '</table>'
    return table
  }

  /**
   * getGeoJSONType returns a lower-cased string indicating the type
   * of features the map contains.
   *
   * @param {Geo} geojson Leaflet Geo object
   * @returns string
   */
  getGeoJSONType(geojson: TopoJSON) {
    if (!geojson?.type) {
      throw new GeoJSONTypeMissingError('Invalid GeoJSON: `null` type property')
    }

    return geojson.type.toLowerCase()
  }

  // Strips XSS and other nasties from a string
  sanitize(content: string | 0) {
    if (content === 0) {
      return '0'
    } // handle edge case where by "0" is sanitized to ""
    return DOMPurify.sanitize(content)
  }
}

export {GeoJSONRenderer}
